// src/RegulatoryOverview.tsx
import React from "react";

/** === Gleiches Theme wie im Connector === */
const theme = {
    bg: "#F2F1ED",
    cardBg: "#FFFFFF",
    border: "#E6E4D9",
    text: "#04242D",
    muted: "#094E5D",
    primary: "#FF6A4C",
    primarySoft: "#FFEAE3",
    primaryBorder: "#FFC4B3",
};

type FrameworkType =
    | "datenschutz"
    | "cyber"
    | "finanz"
    | "cloud"
    | "standard"
    | "branche";

type Framework = {
    id: string;
    title: string;
    short: string;
    description: string;
    audience: string[];
    link: string;
    type: FrameworkType;
};

const TYPE_META: Record<
    FrameworkType,
    { label: string; bg: string; border: string }
> = {
    datenschutz: {
        label: "Datenschutz",
        bg: "rgba(37, 99, 235, 0.08)", // blau
        border: "rgba(37, 99, 235, 0.5)",
    },
    cyber: {
        label: "Cybersicherheit",
        bg: "rgba(220, 38, 38, 0.08)", // rot
        border: "rgba(220, 38, 38, 0.5)",
    },
    finanz: {
        label: "Finanz / RegTech",
        bg: "rgba(147, 51, 234, 0.08)", // lila
        border: "rgba(147, 51, 234, 0.5)",
    },
    cloud: {
        label: "Cloud-Compliance",
        bg: "rgba(8, 145, 178, 0.08)", // cyan
        border: "rgba(8, 145, 178, 0.5)",
    },
    standard: {
        label: "Security-Standard",
        bg: "rgba(55, 65, 81, 0.06)", // grau
        border: "rgba(75, 85, 99, 0.5)",
    },
    branche: {
        label: "Branchenspezifisch",
        bg: "rgba(22, 163, 74, 0.08)", // grün
        border: "rgba(22, 163, 74, 0.5)",
    },
};

const FRAMEWORKS: Framework[] = [
    {
        id: "gdpr",
        title: "GDPR – General Data Protection Regulation",
        short:
            "EU-Datenschutz-Grundverordnung für personenbezogene Daten von EU-Bürgern.",
        description:
            "Die EU-Datenschutz-Grundverordnung (DSGVO) regelt, wie personenbezogene Daten von Personen in der EU erhoben, verarbeitet, gespeichert und gelöscht werden dürfen. Sie gilt extraterritorial – also auch für Nicht-EU-Unternehmen, die Daten von EU-Bürgern verarbeiten.",
        audience: [
            "Alle Unternehmen, die personenbezogene Daten von EU-Bürgern verarbeiten (B2B & B2C)",
            "E-Commerce, SaaS-Anbieter, Healthcare, Public Sector",
            "HR- / Payroll-Dienstleister",
        ],
        link: "https://gdpr-info.eu/",
        type: "datenschutz",
    },
    {
        id: "nis2",
        title: "NIS2 – EU Directive on Network and Information Security",
        short: "EU-Richtlinie für ein hohes gemeinsames Cybersicherheitsniveau.",
        description:
            "NIS2 schafft ein einheitlich hohes Sicherheitsniveau für Netz- und Informationssysteme in der EU. Der Scope wurde stark erweitert – auf deutlich mehr „wesentliche“ und „wichtige“ Einrichtungen in kritischen und hochkritischen Sektoren (z. B. Energie, Verkehr, Gesundheit, Finanzwesen, digitale Infrastruktur).",
        audience: [
            "Betreiber kritischer Infrastrukturen (KRITIS)",
            "Energieversorger, Wasser, Transport, Gesundheitswesen",
            "Banken, Versicherer, digitale Infrastrukturanbieter, große öffentliche Einrichtungen",
        ],
        link: "https://digital-strategy.ec.europa.eu/en/policies/nis2-directive",
        type: "cyber",
    },
    {
        id: "dora",
        title: "DORA – Digital Operational Resilience Act",
        short: "EU-Verordnung zur digitalen Resilienz im Finanzsektor.",
        description:
            "DORA soll sicherstellen, dass Finanzinstitutionen auch bei Cyberangriffen, IT-Ausfällen oder Störungen funktionsfähig bleiben. Kernthemen sind ICT-Risikomanagement, Incident-Reporting, Drittanbieter-Risiken, Resilienztests und Aufsicht über kritische Technologie-Provider.",
        audience: [
            "Banken, Zahlungsdienstleister, Versicherungen, Wertpapierfirmen, Börsen",
            "Cloud- und IT-Dienstleister als „kritische Drittanbieter“ für Finanzinstitute",
            "FinTechs, Custody- und Payment-Provider",
        ],
        link: "https://finance.ec.europa.eu/regulation-and-supervision/financial-services-legislation/digital-operational-resilience-dora_en",
        type: "finanz",
    },
    {
        id: "cra",
        title: "Cyber Resilience Act (CRA)",
        short:
            "EU-Verordnung zu Mindeststandards für Cybersicherheit von Produkten mit digitalen Elementen.",
        description:
            "Der Cyber Resilience Act definiert ein Mindestniveau an Cybersicherheit für nahezu alle „Produkte mit digitalen Elementen“ im EU-Binnenmarkt – von Software bis IoT-Hardware. Er adressiert den gesamten Produktlebenszyklus inklusive Schwachstellenmanagement, Patching und Meldepflichten.",
        audience: [
            "Hersteller und Anbieter von Softwareprodukten, IoT-Geräten und Embedded Systems",
            "Plattformanbieter und SaaS- / PaaS-Lösungen mit EU-Kunden",
            "Unternehmen, die Software in Produkten oder eigenen digitalen Plattformen bereitstellen",
        ],
        link: "https://digital-strategy.ec.europa.eu/en/library/cyber-resilience-act",
        type: "cyber",
    },
    {
        id: "c5",
        title: "BSI C5 – Cloud Computing Compliance Criteria Catalogue",
        short: "Deutscher Kriterienkatalog für sicheres Cloud Computing.",
        description:
            "C5 ist ein vom BSI definierter Kriterienkatalog. Er beschreibt Mindestanforderungen an sicheres Cloud Computing und dient als Prüf- und Nachweisgrundlage für Cloud-Provider und -Kunden – vor allem im öffentlichen Sektor und bei sicherheitskritischen Anwendungen.",
        audience: [
            "Deutsche Behörden und öffentliche Einrichtungen",
            "Unternehmen im regulierten Umfeld (z. B. Government, KRITIS)",
            "Cloud-Provider, die ihre Eignung für deutsche Kunden nachweisen wollen",
        ],
        link: "https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Informationen-und-Empfehlungen/Cloud-Computing/Anforderungen-an-Cloud-Anbieter/C5/c5_node.html",
        type: "cloud",
    },
    {
        id: "iso27001",
        title: "ISO 27001 & verwandte Standards (z. B. TISAX)",
        short:
            "Internationaler Standard für Informationssicherheits-Managementsysteme (ISMS).",
        description:
            "ISO/IEC 27001 definiert Anforderungen an ein Informationssicherheits-Managementsystem (ISMS) und damit an Prozesse, Kontrollen und Risikomanagement rund um Vertraulichkeit, Integrität und Verfügbarkeit von Informationen. TISAX ist ein branchenspezifisches Modell auf ISO-27001-Basis für die Automobilindustrie.",
        audience: [
            "Mittelständische und große Unternehmen mit hohem Schutzbedarf",
            "Automotive, Industrie, Fertigung, Technologie",
            "Dienstleister, die Sicherheits- und Compliance-Nachweise gegenüber Kunden liefern müssen",
        ],
        link: "https://www.iso.org/standard/82875.html",
        type: "standard",
    },
];

const SECTOR_LAYERS: Framework[] = [
    {
        id: "health",
        title: "Gesundheitswesen (EU / national)",
        short:
            "Branchenspezifische Regelungen für medizinische Daten & eHealth-Anwendungen.",
        description:
            "Im Gesundheitssektor greifen zusätzlich zu GDPR nationale Gesundheitsgesetze, eHealth-Rahmenwerke und spezielle medizinische Datenschutzregelungen. Besonders sensibel ist der Umgang mit Patientendaten und Telemedizin-Plattformen.",
        audience: [
            "Krankenhäuser, Kliniken, Labore",
            "Krankenkassen und andere Kostenträger",
            "HealthTech / MedTech, Telemedizin-Plattformen",
        ],
        link: "#",
        type: "branche",
    },
    {
        id: "public",
        title: "Public Sector / Verwaltung",
        short:
            "Spezielle Anforderungen an Staat, Verwaltung und Bildungseinrichtungen.",
        description:
            "Für Behörden und öffentliche Verwaltung gelten zusätzliche E-Government-Gesetze, Vergaberecht und oft verschärfte Datenschutz- und Transparenzanforderungen. Das wirkt sich direkt auf Cloud-Strategien, Betriebsmodelle und Datensouveränität aus.",
        audience: [
            "Ministerien, Landes- und Kommunalverwaltungen",
            "Anstalten des öffentlichen Rechts",
            "Schulen, Hochschulen und weitere Bildungseinrichtungen",
        ],
        link: "#",
        type: "branche",
    },
];

export interface RegulatoryOverviewProps {
    onBack: () => void;
}

const RegulatoryOverview: React.FC<RegulatoryOverviewProps> = ({ onBack }) => {
    return (
        <div
            style={{
                minHeight: "100vh",
                background: theme.bg,
            }}
        >
            {/* HEADER */}
            <header
                style={{
                    position: "sticky",
                    top: 0,
                    background: "rgba(255,255,255,0.9)",
                    borderBottom: `1px solid ${theme.border}`,
                    backdropFilter: "blur(6px)",
                    padding: "10px 16px",
                    zIndex: 1000,
                }}
            >
                <div
                    style={{
                        maxWidth: 1200,
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                    }}
                >
                    <button
                        onClick={onBack}
                        style={{
                            border: `1px solid ${theme.primaryBorder}`,
                            background: theme.primarySoft,
                            padding: "6px 12px",
                            cursor: "pointer",
                            borderRadius: 6,
                            fontWeight: 600,
                        }}
                    >
                        ← Zurück
                    </button>

                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: theme.text,
                            textAlign: "center",
                            flex: 1,
                        }}
                    >
                        Regulatorische Frameworks
                    </div>

                    {/* Platzhalter für symmetrisches Layout */}
                    <div style={{ width: 80 }} />
                </div>
            </header>

            {/* CONTENT */}
            <div
                style={{
                    maxWidth: 1100,
                    margin: "20px auto",
                    padding: "0 16px 40px",
                }}
            >
                <h2 style={{ fontSize: 26, marginBottom: 8 }}>
                    Überblick über EU-Regulatorik
                </h2>
                <p style={{ color: theme.muted, marginBottom: 24 }}>
                    Diese Übersicht zeigt zentrale Rahmenwerke rund um Datenschutz,
                    Cybersicherheit, Betriebssicherheit und branchenspezifische Vorgaben,
                    die bei Cloud- und IT-Architekturen eine Rolle spielen.
                </p>

                {/* Haupt-Rahmenwerke als Kacheln */}
                <h3 style={{ marginTop: 0, marginBottom: 12 }}>
                    Übergreifende Rahmenwerke
                </h3>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: "32px 16px", // 32px vertikal, 16px horizontal
                        alignItems: "stretch",
                        marginBottom: 32,
                    }}
                >
                    {FRAMEWORKS.map((fw) => {
                        const meta = TYPE_META[fw.type];
                        return (
                            <div
                                key={fw.id}
                                style={{
                                    background: theme.cardBg,
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: 10,
                                    padding: 16,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                }}
                            >
                                {/* Titel + Typ-Badge */}
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 8,
                                        marginBottom: 6,
                                    }}
                                >
                                    <h4
                                        style={{
                                            margin: 0,
                                            fontSize: 16,
                                            fontWeight: 700,
                                            color: theme.text,
                                        }}
                                    >
                                        {fw.title}
                                    </h4>
                                    <span
                                        style={{
                                            padding: "3px 8px",
                                            borderRadius: 9999,
                                            fontSize: 11,
                                            border: `1px solid ${meta.border}`,
                                            background: meta.bg,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {meta.label}
                                    </span>
                                </div>

                                <p
                                    style={{
                                        marginTop: 4,
                                        marginBottom: 8,
                                        fontSize: 13,
                                        color: theme.muted,
                                    }}
                                >
                                    {fw.short}
                                </p>

                                <p
                                    style={{
                                        marginTop: 4,
                                        marginBottom: 10,
                                        fontSize: 14,
                                    }}
                                >
                                    {fw.description}
                                </p>

                                <div style={{ marginTop: "auto" }}>
                                    <div style={{ marginTop: 8 }}>
                                        <b>Typischerweise betroffen:</b>
                                        <ul
                                            style={{
                                                paddingLeft: 18,
                                                marginTop: 4,
                                                marginBottom: 8,
                                                fontSize: 13,
                                            }}
                                        >
                                            {fw.audience.map((a, i) => (
                                                <li key={i}>{a}</li>
                                            ))}
                                        </ul>
                                    </div>

                                    <a
                                        href={fw.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: "inline-block",
                                            marginTop: 6,
                                            padding: "6px 10px",
                                            borderRadius: 6,
                                            border: `1px solid ${theme.primaryBorder}`,
                                            background: theme.primarySoft,
                                            fontWeight: 600,
                                            textDecoration: "none",
                                            color: theme.text,
                                        }}
                                    >
                                        Mehr erfahren →
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Branchenspezifische Layer als Kacheln */}
                <h3 style={{ marginTop: 60, marginBottom: 12 }}>
                    Branchenspezifische Layer
                </h3>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: "32px 16px", // 32px vertikal, 16px horizontal
                    }}
                >
                    {SECTOR_LAYERS.map((fw) => {
                        const meta = TYPE_META[fw.type];
                        return (
                            <div
                                key={fw.id}
                                style={{
                                    background: theme.cardBg,
                                    border: `1px solid ${theme.border}`,
                                    borderRadius: 10,
                                    padding: 16,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 8,
                                        marginBottom: 6,
                                    }}
                                >
                                    <h4
                                        style={{
                                            margin: 0,
                                            fontSize: 16,
                                            fontWeight: 700,
                                            color: theme.text,
                                        }}
                                    >
                                        {fw.title}
                                    </h4>
                                    <span
                                        style={{
                                            padding: "3px 8px",
                                            borderRadius: 9999,
                                            fontSize: 11,
                                            border: `1px solid ${meta.border}`,
                                            background: meta.bg,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {meta.label}
                                    </span>
                                </div>

                                <p
                                    style={{
                                        marginTop: 4,
                                        marginBottom: 8,
                                        fontSize: 13,
                                        color: theme.muted,
                                    }}
                                >
                                    {fw.short}
                                </p>

                                <p
                                    style={{
                                        marginTop: 4,
                                        marginBottom: 10,
                                        fontSize: 14,
                                    }}
                                >
                                    {fw.description}
                                </p>

                                <div style={{ marginTop: "auto" }}>
                                    <div style={{ marginTop: 8 }}>
                                        <b>Typischerweise betroffen:</b>
                                        <ul
                                            style={{
                                                paddingLeft: 18,
                                                marginTop: 4,
                                                marginBottom: 8,
                                                fontSize: 13,
                                            }}
                                        >
                                            {fw.audience.map((a, i) => (
                                                <li key={i}>{a}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default RegulatoryOverview;